import type * as React from "react";
import { type ChangeEvent, memo, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deletePackageEntry,
  navigateToProductContractAction,
  selectPackaging,
  toggleCommentVisibility,
  toggleShowPackaging,
  updateComment,
  updateDate,
  updatePoNumber,
  updatePriceReference,
  updateQuantity,
  updateSalesEnabled,
} from "../../../../redux/actions/cartPageActions";
import { MAX_QUANTITY_LENGTH } from "../../../../../../../utils/constants";
import { type LineItem, PackageEntrySource } from "../../../../models/CartData";
import * as PriceUtils from "../../../../../../../utils/PriceUtils";

import classNames from "classnames";
import { isValidQuantity } from "../../../../../../../utils/packageEntryUtils";
import { selectProductDescriptor } from "../../../../../../../redux/products/productsSelectors";
import DeleteButton from "../DeleteButton";
import CartQuantityInput from "../CartQuantityInput";
import { CommentButton } from "../../../../../../../QuoteCart/components/ActiveQuoteEdit/ProductTable/CommentButton";
import GridGappedContainer, {
  GridGappedContainerSize,
} from "../../../../../../../common/components/GridGappedContainer/GridGappedContainer";
import CircleSpinner from "../../../../../../../common/components/CircleSpinner/CircleSpinner";
import { selectProperAmountInCart } from "../../../../../../../AddToCart/redux/cartAmounts/cartAmountSelectors";
import { type RootState } from "../../../../../../../redux/store";
import { Trans, useTranslation } from "react-i18next";
import Parameter from "../../../../../../../common/components/Parameter";
import SimplePriceText, {
  PriceTextVariants,
} from "../../../../../../../common/components/SimplePriceText/SimplePriceText";
import OptimeSolutionsMessage from "../../../../../../../common/components/Messages/OptimeSolutionsMessage";
import { canUseCommentsForProduct } from "../../../../../../../utils/productUtils";
import { clearManualDate } from "../../../../redux/actions/cartQuoteCommonActions";
import { EditablePackagingCell, ViewablePackagingCell } from "./PackagingCell";
import { Card } from "../../../../../../../Configurators/Linear/components/Card";
import {
  AliasesParameter,
  GPSDParameter,
  IncludedSurcharge,
} from "../../../../../../../common/components/Product/ProductParams";
import { ContractItemParam, QuoteItemParam } from "../../LineItemStaticParams";
import { PriceReferenceParam } from "../../../LineItemEditableParams";
import { type LineItemMessage } from "../../../../../../../utils/cartUpdating/common";
import { useOnMount } from "../../../../../../../common/hooks/useOnMount";
import { C_SURFACE } from "../../../../../../../common/components/icon/Colors";
import { Stack, styled } from "@mui/material";
import { PackagingSelection } from "../../../../../../../common/components/PackagingSelection/PackagingSelection";
import CenterContents from "../../../../../../../common/components/CenterContents";
import { Collapse } from "~/common/components/Collapse";
import { canSeePriceRefColumn } from "../../../../../../../QuickEntryPopup/helpers";
import {
  getEDDLabel,
  isMinOrderQuantityRestrictionEnabled,
  isTariffSurchargeEnabled,
} from "~/feature/cart/cart/utils/cartUtils";
import {
  getPriceInfo,
  getSurchargeValueFromTotalPrice,
  getTotalPriceForLineItem,
  isProductPMA,
  productHasInStock,
} from "../../../../../../ProductDescriptor/v1/productDescriptorUtils";
import { cartIsDayDisabled } from "../../../../../../../utils/dateUtils";
import { isKITKITProduct } from "../../../../../../../models/ProductData";
import { MaterialInput } from "../../../../../../../common/components/materialInput/MaterialInput";
import { isLineLevelCustomerOrderNumberEnabled } from "../../../../../../../utils/features";
import { PartialDeliveriesSection } from "./PartialDeliveriesSection";
import { isSpecialPrice } from "~/ProductDetails/redux/productDetailsSelectors";

import { PHASE_IN_BUY_ERROR_KEY } from "~/ProductDetails/components/PhaseInOut/constants";
import { InfoProductMessagePMA } from "~/ProductDetails/components/ProductSummary/InfoProductMessagePMA";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { SaleQuantityIndicator } from "~/SalesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";
import { Price as PriceV3, type PriceProps } from "~/common/components/Price/v3/Price";
import { CART_QTY_EXCEEDS_SALES_ERROR_KEY } from "~/SalesCampaign/constants";
import { LineItemStockIndicator } from "~/feature/cart/cart/components/CartPage/ProductTable/EditableEntry/LineItemStockIndicator";
import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { PackagingSelectionVariant } from "~/common/components/PackagingSelection/PackagingSelectionVariant";
import { Alert } from "~/common/m3/Alert/Alert";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import DateInput from "~/common/components/DateInput";
import { DateFormatType, formatDate } from "~/utils/DateFormatter";
import { useSalesHelper } from "~/SalesCampaign/salesHelper";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { processPricingDetails } from "~/SalesCampaign/utils";
import type { NetPriceData } from "~/models/PriceData";
import { SaleDiscountIndicator } from "~/SalesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import { PriceElements } from "~/common/components/Price/PriceV4";
import { useAlternateProducts } from "~/feature/cart/cart/AlternateProductsContext";
import classes from "./EditablePackageEntry.module.less";
import {
  findMinOrderQuantity,
  getDefaultQuantity,
  useMOQValidation,
} from "~/ProductDetails/components/ProductSummary/common/usePdpSummaryLogic";
import { useCartPageContext } from "~/feature/cart/cart/CartPageContext";

interface SelfProps {
  packEntryId: number;
  index: number;
  onRender?: () => void;
  onDelete: () => void;
  className?: string;
  packageIndex?: number;
  isLastIndex?: boolean;
}

const EditableLineItem = ({
  packEntryId,
  index,
  onRender,
  className,
  onDelete,
  packageIndex,
  isLastIndex,
}: SelfProps) => {
  const { t } = useTranslation();
  const packEntry = useSelector(selectLineItem(packEntryId));
  const product = useSelector(selectProduct(packEntryId));

  const priceUnit = packEntry.netPrice?.unit ?? 1;

  const canSeeComment = canUseCommentsForProduct(product.base.marketType);
  const quantityVisible = product.base.quantityVisible;
  const isOptime = product.base.isOptime;
  const { scrollIndex, setScrollIndex } = useAlternateProducts();
  const [showBorder, setShowBorder] = useState(scrollIndex == packageIndex && isLastIndex);

  const validQty = isValidQuantity(packEntry.packSize, packEntry.quantity);
  const minOrderQuantity = findMinOrderQuantity(product, packEntry.packCode);

  const salesHelper = useSalesHelper(product);
  const salesStock = salesHelper.forPackage(packEntry.packCode)?.stock ?? 0;
  const isOnSale = salesHelper.isPackageOnSale(packEntry.packCode);
  const saleActive = packEntry.saleCampaignEnabled;
  const { setDisableCheckout } = useCartPageContext();
  const packSize = packEntry?.packSize ?? 1;

  const { isPackageSizeInvalid, MOQHelperLabel } = useMOQValidation({
    quantity: packEntry.quantity,
    packSize,
    minOrderQuantity,
    isAvailabilityLoaded: !!product.availability,
    isPackageSelected: !!packEntry.packCode,
    isOnSale: saleActive,
  });

  // const isEditMode = !data.disableEdit;
  const isBulkUploadEntry = packEntry.source === PackageEntrySource.BULK;
  const inStock = productHasInStock(product);

  let totalPrice;
  let surchargeValue;
  let hasSurcharge;

  const netPrice = packEntry.netPrice;
  if (isBulkUploadEntry || !netPrice) {
    totalPrice = "...";
    surchargeValue = undefined;
    hasSurcharge = false;
  } else {
    hasSurcharge = (packEntry.netPrice?.surcharge ?? 0) > 0;
    const totalPriceVal = getTotalPriceForLineItem(packEntry, product);

    surchargeValue = hasSurcharge ? getSurchargeValueFromTotalPrice(totalPriceVal) : undefined;
    if (!(netPrice && netPrice.value > 0)) {
      hasSurcharge = false;
    }
    totalPrice = PriceUtils.text(totalPriceVal, "-");
  }

  const tariffSurcharge = isTariffSurchargeEnabled() && packEntry.netPrice?.tariffSurcharge;

  const showBetterDeal = useSelector(selectShowBetterDeal(product, packEntry));

  // TODO: Move these messages to the CartRefresh logic / line item update logic
  let messages = packEntry.messages.slice();
  if (showBetterDeal) {
    messages.push({
      type: "info",
      key: "addProduct.scalingDeal",
    });
  }

  // TODO: Combine contract and regular messages. Make the filter nicer.
  const fatalMessages = messages.filter((msg) => msg.type === "fatal");
  if (fatalMessages.length > 0 || (packEntry.contractErrorMessage && packEntry.contractErrorMessage.type === "fatal")) {
    messages = fatalMessages;
  }

  const isAddedFromQuote = packEntry.quoteLine !== "" && packEntry.quoteNumber !== "";
  // const isLCProduct = isConfiguratorProduct(product);

  const isMOQCheckEnabled = isMinOrderQuantityRestrictionEnabled() && !saleActive && !isAddedFromQuote;
  let qtyIsError = false;
  const isSizeInvalid = !isMOQCheckEnabled ? !validQty : isPackageSizeInvalid;
  if (isSizeInvalid) {
    qtyIsError = true;
  } else if (messages.find((m) => m.key === PHASE_IN_BUY_ERROR_KEY)) {
    qtyIsError = true;
  } else if (messages.find((m) => m.key === CART_QTY_EXCEEDS_SALES_ERROR_KEY)) {
    qtyIsError = true;
  }

  useEffect(() => {
    if (isMOQCheckEnabled && packEntry.quantity < minOrderQuantity) {
      setDisableCheckout(true);
    } else {
      setDisableCheckout(false);
    }
  }, [packEntry.quantity, minOrderQuantity, setDisableCheckout]);

  const {
    isDayDisabled,
    clearDate,
    updateDate,
    updateComment,
    toggleShowComment,
    updateQty,
    updatePONumber,
    removeEntry,
    updatePriceReference,
    onPackagingClick,
    closePackaging,
    selectPackage,
    updateSalesEnabled,
  } = useLineItemActions(packEntry, product);

  const disableEditing = !!packEntry.messages.find((msg) => msg.type === "fatal");

  useOnMount(() => {
    if (!product.base.quantityVisible && !disableEditing) {
      updateDate(new Date());
    }
  });

  useEffect(() => {
    if (onRender) {
      onRender();
    }
  }, [packEntry, product]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowBorder(false);
      setScrollIndex(-1);
    }, 1500);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  // contract set max year date chooser
  let maxYear;
  if (PackageEntrySource.CONTRACT === packEntry.source && packEntry.maxRdd) {
    maxYear = packEntry.maxRdd.getFullYear();
  }

  // manual trigger update date to bypass calendar error
  if (isProductPMA(product) && !packEntry.deliveryDate) {
    updateDate(new Date());
  }

  const isSpecial = isSpecialPrice(product, packEntry.packCode);

  const showEdd = true;

  const testIdDate = `cart-date-dropdown-${index}`;

  const packageentryquantityinput = `package-entry-quantity-input-${index}`;

  const btntogglecomment = `btn-toggle-comment-${index}`;

  const packageentrycommentinput = `package-entry--comment-input-${index}`;

  const tLabel = (key: string) => t(`cartPage.table.content.${key}`);

  const disableWS = window.SCHA.config.disableAllWS;

  const zeroPriceLabel = isProductPMA(product) || isKITKITProduct(product.base) ? "-" : t("product.priceOnRequest");

  let priceEl;
  if (disableWS) {
    priceEl = null;
  } else if (product.isPriceLoading) {
    priceEl = <CircleSpinner />;
  } else if (saleActive) {
    const { price, netPrice, oldPrice, discountFormatted } = processPricingDetails({
      netPrice: packEntry.actualNetPrice as NetPriceData,
      salesPrice: packEntry.actualSalePrice!,
      saleActivated: saleActive,
    });

    priceEl = (
      <Stack>
        <Stack direction="row" gap={1} alignItems={"center"}>
          <CurrentPrice price={price} zeroText={zeroPriceLabel} tariffSurcharge={tariffSurcharge} />
          {discountFormatted && <SaleDiscountIndicator.V2 discountFormatted={discountFormatted} />}
        </Stack>
        {oldPrice && <OldPrice price={oldPrice} tariffSurcharge={tariffSurcharge} />}
        <PriceV3.NetPriceUnit price={netPrice} variant={"short"} />
      </Stack>
    );
  } else {
    priceEl = (
      <PriceElements.Stack gap={0}>
        <PriceElements.Price price={netPrice} zeroText={zeroPriceLabel} tariffSurcharge={tariffSurcharge} />
        <PriceElements.NetPriceLabel price={netPrice} labelVariant="short" color="textSecondary" />
      </PriceElements.Stack>
    );
  }

  const [commentRef, toggleComment] = useShowAndScrollInput(toggleShowComment);
  const minQtyValue = isMOQCheckEnabled ? getDefaultQuantity(packSize, minOrderQuantity) : packSize;
  const qtyHelperLabel = isMOQCheckEnabled ? MOQHelperLabel : undefined;

  return (
    <Card
      data-test-id={`package-entry`}
      disableHover={true}
      className={classNames(className, !packEntry.valid && "js__invalid-pack", showBorder && classes.highlight)}
    >
      <div className={"py-3"}>
        <CellRow>
          <Cell first={true} className={"scha__line-item__cell--qty"}>
            {quantityVisible ? (
              <CartQuantityInput
                className={"w-100 quantity"}
                label={t("input.quantity")}
                value={packEntry.quantity}
                onChange={updateQty}
                allowedLength={MAX_QUANTITY_LENGTH}
                error={qtyIsError}
                data-test-id={packageentryquantityinput}
                disabled={disableEditing}
                step={packEntry.packSize}
                helperLabel={qtyHelperLabel}
                min={minQtyValue}
              />
            ) : (
              <Parameter
                label={t("input.quantity")}
                value={packEntry.quantity}
                data-test-id={"package-entry-quantity"}
              />
            )}
            {salesStock > 0 && saleActive && <SaleQuantityIndicator.V2 quantity={salesStock} />}
          </Cell>

          <Cell className={"scha__line-item__cell"}>
            {packEntry.restrictedPackage ? (
              <ViewablePackagingCell product={product} lineItem={packEntry} />
            ) : (
              <EditablePackagingCell
                product={product}
                lineItem={packEntry}
                openEdit={onPackagingClick}
                isPackagingSectionOpen={packEntry.isPackagingSelectionOpen}
              />
            )}
            <AliasesParameter product={product.base} mode={"15-digits"} packCode={packEntry.packCode} />

            {isLineLevelCustomerOrderNumberEnabled() && (
              <div className={"w-100"}>
                <MaterialInput
                  label={t("search.type.customerOrderNumber")}
                  data-test-id={"package-entry-po-number"}
                  value={packEntry.customerOrderNumber}
                  onChange={updatePONumber}
                />
              </div>
            )}
          </Cell>

          <Cell className={"scha__line-item__cell"}>
            {quantityVisible && !isProductPMA(product) ? (
              <DateInput
                data-test-id={testIdDate}
                label={tLabel("chooseDate")}
                helperLabel={showEdd ? getEDDLabel(packEntry, t) : "-"}
                date={packEntry.manuallySetRDD ? packEntry.deliveryDate : null}
                placeholder={
                  !packEntry.manuallySetRDD && packEntry.deliveryDate
                    ? formatDate(packEntry.deliveryDate, DateFormatType.DATE)
                    : undefined
                }
                onChange={updateDate}
                onClear={clearDate}
                showClear={packEntry.manuallySetRDD}
                isInvalid={!packEntry.deliveryDate}
                isDayDisabled={isDayDisabled}
                disabled={disableEditing}
                maxYear={maxYear}
              />
            ) : (
              <div>{"-"}</div>
            )}
          </Cell>

          <Cell className={"scha__line-item__cell"}>
            <LineItemStockIndicator product={product} packEntry={packEntry} />
          </Cell>

          <Cell className={"scha__line-item__cell"}>
            {priceEl}

            <CheckboxWithLabel
              checked={saleActive}
              onCheck={updateSalesEnabled}
              hidden={!isOnSale || !!packEntry.priceReference}
            >
              {t("feature.salesCampaign.specialDealPrice")}
            </CheckboxWithLabel>

            <QuoteItemParam product={product.base} lineItem={packEntry} />
            <ContractItemParam product={product.base} lineItem={packEntry} />

            <PriceReferenceParam
              product={product.base}
              priceReference={packEntry.priceReference}
              onChange={updatePriceReference}
              data-test-id={"package-entry-price-reference"}
              disabled={disableEditing || saleActive}
              editable={!packEntry.quoteNumber && canSeePriceRefColumn()}
            />
          </Cell>

          <Cell className={"scha__line-item__cell"}>
            <strong data-test-id={"package-entry-total-price"}>
              {!disableWS && totalPrice}
              {!disableWS && hasSurcharge && "*"}
            </strong>
            {!quantityVisible && (
              <div className={"mt-1 f-label-color"}>
                <SimplePriceText
                  priceUnit={priceUnit}
                  variant={quantityVisible ? null : PriceTextVariants.yearlySubscription}
                />
              </div>
            )}

            <IncludedSurcharge value={surchargeValue} percent={packEntry.netPrice?.surcharge ?? 0} />

            <GPSDParameter product={product.base} />
          </Cell>

          <Cell alignEnd={true} last={true}>
            <GridGappedContainer gapSize={GridGappedContainerSize.S_16}>
              {canSeeComment && !disableEditing && (
                <div data-test-id={btntogglecomment}>
                  <CommentButton
                    onClick={toggleComment}
                    open={packEntry.showComments}
                    fontSize={"small"}
                    className={"scha__saved-configurations__comment-button"}
                  />
                </div>
              )}
              <DeleteButton
                onClick={() => {
                  removeEntry();
                  if (!inStock) {
                    onDelete();
                  }
                }}
                disabled={packEntry.isBundled && packEntry.original}
              />
            </GridGappedContainer>
          </Cell>
        </CellRow>

        {packEntry.contractErrorMessage && (
          <Row className={"mt-3"}>
            <ContractErrorMessage msg={packEntry.contractErrorMessage} packEntryId={packEntry.id} />
          </Row>
        )}
        {packEntry.showComments && (
          <Row className={"mt-3"}>
            <div data-test-id={packageentrycommentinput}>
              <CommentInput
                value={packEntry.comment || ""}
                onChange={updateComment}
                ref={commentRef}
                label={t("wishlistPage.comment.label")}
              />
            </div>
          </Row>
        )}
        {/* spacer without border */}
        {(!quantityVisible || isOptime || messages.length > 0) && <div className={"mt-2 mb-0"} />}
        <Stack gap={1} padding={2}>
          {messages.map((message) => (
            <Alert key={message.key} severity={message.type === "fatal" ? "error" : message.type}>
              {t(message.key, message.params)}
            </Alert>
          ))}
          {isProductPMA(product) && <InfoProductMessagePMA />}
          {!quantityVisible && <Alert severity="info">{t("subscriptionProduct.DST.billingInfo")}</Alert>}
          {isOptime && <OptimeSolutionsMessage />}
        </Stack>
      </div>

      <Collapse in={packEntry.isPackagingSelectionOpen}>
        <PackagingArea disableHover={true}>
          {product.isLoading && (
            <CenterContents className={"my-30"}>
              <CircleSpinner />
            </CenterContents>
          )}
          {!disableWS && product.isError && <CenterContents className={"my-30"}>{t("hybrisAjaxError")}</CenterContents>}
          {product.isLoaded && (
            <PackagingSelection
              selectedCode={packEntry.packCode}
              onSelectCode={selectPackage}
              product={product}
              variant={PackagingSelectionVariant.SRP}
            />
          )}
        </PackagingArea>
      </Collapse>

      <PartialDeliveriesSection
        partialDeliveries={packEntry.partialDeliveries}
        packagingIsExpanded={packEntry.isPackagingSelectionOpen}
      />
    </Card>
  );
};

export default memo(EditableLineItem);

const CellRow = ({ children }: { children?: ReactNode }) => (
  <div className={"scha__cart__product-table__right-side__col-container"}>{children}</div>
);

export const PackagingArea = styled(Card)({
  background: C_SURFACE,
  padding: "20px",
});

const Cell: React.FunctionComponent<{
  children?: ReactNode;
  alignEnd?: boolean;
  first?: boolean;
  last?: boolean;
  className?: string;
}> = ({ children, alignEnd, first, last, className }) => {
  const cls = classNames(
    "scha__cart__product-table__body-cell",
    {
      "first-on-right": first,
      "last-on-right": last,
      "flex-justify-end d-flex": alignEnd,
    },
    className,
  );
  return <div className={cls}>{children}</div>;
};

const Row: React.FunctionComponent<{
  className?: string;
  children?: ReactNode;
}> = ({ children, className }) => <div className={classNames(className, "px-3")}>{children}</div>;

const ContractErrorMessage = ({ msg, packEntryId }: { msg: LineItemMessage; packEntryId: number }) => {
  const dispatch = useDispatch();
  const onClick: React.MouseEventHandler = (e) => {
    e.preventDefault();
    dispatch(navigateToProductContractAction(packEntryId));
  };

  return (
    <Alert severity={msg.type === "fatal" ? "error" : msg.type} className="mt-3">
      <Trans i18nKey={"cartPage.error.regular.contractExpiredDifferentActive"}>
        This product is part of the contract but not matching the assigned characteristics. Please delete this product
        and select it from the{" "}
        <a href="#" onClick={onClick}>
          Contract Page
        </a>
      </Trans>
    </Alert>
  );
};

const useLineItemActions = (lineItem: LineItem, product: ProductDescriptor) => {
  const { actions: generalActions, dataRef, dispatch } = useAbstractLineItemActions(lineItem, product);

  const actions = useMemo(() => {
    const packEntryId = dataRef.current.lineItem.id;
    return {
      toggleShowPackaging: () => dispatch(toggleShowPackaging(packEntryId)),
      closePackaging: () => dispatch(toggleShowPackaging(packEntryId, false)),
      selectPackage: (packCode: string) => dispatch(selectPackaging(packEntryId, packCode)),
      updateSalesEnabled: (salesEnabled: boolean) =>
        dispatch(updateSalesEnabled(salesEnabled, dataRef.current.lineItem.id)),
    };
  }, []);

  return {
    ...generalActions,
    onPackagingClick: actions.toggleShowPackaging,
    selectPackage: actions.selectPackage,
    closePackaging: actions.closePackaging,
    updateSalesEnabled: actions.updateSalesEnabled,
  };
};

export const useAbstractLineItemActions = (lineItem: LineItem, product: ProductDescriptor) => {
  const dataRef = useRef({ lineItem, product });
  dataRef.current = { lineItem, product };

  const dispatch = useDispatch();

  const actions = useMemo(
    () => ({
      isDayDisabled: (date: Date) => cartIsDayDisabled(dataRef.current.lineItem, date),
      updateDate: (date: Date) => dispatch(updateDate(date, dataRef.current.lineItem.id)),
      clearDate: () => dispatch(clearManualDate(dataRef.current.lineItem.id)),

      updateQty: (e: number) => {
        if (e !== undefined && e !== null) {
          dispatch(updateQuantity(e, dataRef.current.lineItem.id));
        }
      },
      updateComment: (comment: string) => dispatch(updateComment(comment, dataRef.current.lineItem.id)),
      updatePriceReference: (priceRef: string | undefined) =>
        dispatch(updatePriceReference(priceRef || "", dataRef.current.lineItem.id)),
      updatePONumber: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
        dispatch(updatePoNumber(e.target.value, dataRef.current.lineItem.id)),
      removeEntry: () => dispatch(deletePackageEntry(dataRef.current.lineItem.id)),
      toggleShowComment: () => dispatch(toggleCommentVisibility(dataRef.current.lineItem.id)),
    }),
    [],
  );

  return {
    actions,
    dataRef,
    dispatch,
  };
};

export const selectLineItem = (packEntryId: number) => (state: RootState) =>
  state.cart.packEntriesById[packEntryId] as LineItem;
export const selectProduct = (packEntryId: number) => (state: RootState) => {
  const packEntry = state.cart.packEntriesById[packEntryId];

  return selectProductDescriptor(state, {
    productCode: packEntry.productCode,
    priceReference: packEntry.priceReference,
  });
};

const selectShowBetterDeal = (product: ProductDescriptor, packEntry: LineItem) => (state: RootState) => {
  const qtyInCart = selectProperAmountInCart({
    productCode: product.base.code,
    packCode: packEntry.packCode,
    priceReference: packEntry.priceReference,
  })(state);

  let showBetterDeal = false;
  const priceInfo = getPriceInfo({ product, packCode: packEntry.packCode });

  if (priceInfo && priceInfo.minQtyAvailableScale !== null) {
    showBetterDeal = qtyInCart >= priceInfo.minQtyAvailableScale;
  }

  return showBetterDeal;
};

const CurrentPrice = styled(PriceV3, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(PriceV3, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  ...getFontStyle(FontSize.BodySmall),
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));
